import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  Optional,
  Self,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  exhaustMap,
  map,
  startWith,
  tap,
} from 'rxjs';
import { DescontoService } from '../../services/desconto/desconto.service';
import { ProdutoDesconto } from '@brunsker/libs/portal-do-vendedor/utils/portal-do-vendedor-new-api/lib/models/product.models';
import { UtilsBsHotToastService } from '@brunsker/libs/core/utils/hot-toast/src';
import { NewApiOrcamentoService } from '@brunsker/libs/portal-do-vendedor/utils/portal-do-vendedor-new-api/lib/services/api/new-api-orcamento/new-api-orcamento.service';
import { ResquestBudget } from '@brunsker/libs/portal-do-vendedor/utils/portal-do-vendedor-new-api/lib/models/orcamento.models';
import {
  discountToPercentage,
  parseDiscount,
  percentageToDiscount,
  roundDiscount,
  signedDiscount,
} from './discount-calculation';

import {
  DescontoPrefix,
  DescontoNumero,
  DescontoEntrada,
  DescontoChange,
  DescontoEvento,
  IDescontoRodape,
  RodaPe,
} from '../../models/desconto.models';

@UntilDestroy()
@Component({
  selector: 'bs-desconto',
  templateUrl: './desconto.component.html',
  styleUrls: ['./desconto.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DescontoComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  value!: DescontoEntrada;

  private onChange: DescontoChange = () => undefined;
  onTouched: () => void = () => undefined;
  realPrefix: 'R$' = 'R$';
  percentPrefix: '%' = '%';
  readonly decimalMarkers: ['.', ','] = ['.', ','];
  signal: string = '+';
  @Input() context: 'cart' | 'product' | 'footer' = 'cart';
  @Input() sellingValueControl?: FormControl;
  @Input() blocked = false;
  @Input() inputId?: string;
  @Input() inputLabel = 'Desconto ou acréscimo';
  @Output() confirmed = new EventEmitter<IDescontoRodape>();
  @Output() prefixChange = new EventEmitter<DescontoPrefix>();
  @Output() blockedInteraction = new EventEmitter<void>();
  private disabled = false;
  private productPercentage = 0;

  descontoRodaPePrefix: DescontoPrefix = '%';
  descautomatico!: string;
  unconfirmedValue!: DescontoNumero;
  unconfirmedRodaPeValue: string = '';

  writeValue(
    value: DescontoEntrada,
  ): void {
    this.value = value;

    if (this.context === 'product') {
      this.productPercentage = -parseDiscount(value);
      this.syncProductInput();
      return;
    }

    const controlValue =
      typeof value === 'object' && value !== null ? value.value : value;

    if (typeof value === 'object' && value?.prefix) {
      this.matPrefix = value.prefix;
    }

    const normalizedValue = controlValue ?? 0;
    if (this.descontoControl.value !== normalizedValue) {
      this.descontoControl.setValue(normalizedValue, { emitEvent: false });
    }
    this.changeDetectorRef.markForCheck();
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    disabled
      ? this.descontoControl.disable({ emitEvent: false })
      : this.descontoControl.enable({ emitEvent: false });
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: DescontoChange): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  @Input() id!: string;
  @Input() oldValue!: number;
  @Input() unitaryValue!: number;
  @Input() descontoRodaPe!: FormControl;
  @Input() currentDiscount!: number;
  @Input('maxPercentDiscount') maxPercentDiscount!: ProdutoDesconto;
  numautorizacao: number[] = [];
  descontoControl = new FormControl();
  matPrefix: DescontoPrefix = '%';
  lastRealValue!: string;
  lastPercentValue!: string;
  maxPercent!: number;
  maxReal!: number;
  isConfirmed$$ = new BehaviorSubject(true);
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    //this.applyDiscout(event);
  }

  constructor(
    @Optional() @Self() public readonly ngControl: NgControl | null,
    private readonly toast: UtilsBsHotToastService,
    private readonly router: Router,
    private readonly descontoService: DescontoService,
    private readonly orcamentoService: NewApiOrcamentoService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    if (this.ngControl) this.ngControl.valueAccessor = this;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['maxPercentDiscount'] || changes['unitaryValue']) {
      this.updateMaximumDiscountValues();
      if (this.context === 'product') this.syncProductInput();
    }
  }

  ngOnInit(): void {
    if (this.context !== 'cart') {
      if (this.context === 'product') {
        this.syncProductInput();
        this.sellingValueControl?.valueChanges
          .pipe(untilDestroyed(this))
          .subscribe(() => this.syncProductInput());
      }
      return;
    }
    if (!this.oldValue) {
      this.oldValue = 0;
    }
    if (
      this.oldValue &&
      this.oldValue != 0 &&
      !sessionStorage.getItem(this.id)
    ) {
      sessionStorage.setItem(this.id, this.oldValue.toString());
    }

    const numericOldValue = Number(this.oldValue ?? 0);
    this.signal =
      numericOldValue < 0 || Object.is(numericOldValue, -0) ? '+' : '-';
    this.isConfirmed$$.next(true);
    if (!this.descontoRodaPe.value) {
      this.descontoRodaPe.setValue({
        value: 0,
        prefix: this.matPrefix,
      });
    }
    this.setMaxDiscounts();

    this.setDescontoControlValueChanges();

    this.rodaPeValueChanges();
  }

  private setDescontoControlValueChanges() {
    this.descontoControl.valueChanges
      .pipe(
        untilDestroyed(this),
        startWith(this.currentDiscount),
        map((value) => value?.toString()),
        tap((value) => {
          this.unconfirmedValue = value;
          const contPrefix = this.descontoService.PrefixRodapeCont$;

          if (contPrefix.value > 0 && this.matPrefix == '%') {
            contPrefix.setValue(contPrefix.value - 1);
          }
          if (
            (!!this.descontoService.descontoRodapeValue$?.value?.value ||
              this.descontoService.descontoRodapeValue$.value + '' == '') &&
            this.descontoService.descontoRodapeValue$.value.value != 0
          ) {
            this.descontoControl.disable({ emitEvent: false });
            if (this.matPrefix == 'R$')
              this.matPrefix =
                this.matPrefix === this.realPrefix
                  ? this.percentPrefix
                  : this.realPrefix;
          } else if (!this.disabled) {
            this.descontoControl.enable({ emitEvent: false });
          }
        }),
        exhaustMap((value) => {
          return this.isConfirmed$$.pipe(
            map(() => {
              return value;
            }),
          );
        }),
      )
      .subscribe((value) => {
        //  console.log("🚀 ->",
        //    this.isConfirmed$$.value,
        //    'isConfirmed',
        //    value,
        //    typeof value,
        //    this.descontoRodaPe.value,
        //    this.unconfirmedValue
        //  );

        value = this.isConfirmed$$.value ? this.unconfirmedValue : value;

        if (!this.isConfirmed$$.value) return;

        value = typeof value == 'string' ? value.replace(',', '.') : value;

        this.writeDescontoControl(value);
      });
  }

  setOldValue(rodaPeValue: DescontoNumero | null) {
    if (this.router.url.includes('carrinho')) return;
    const value =
      typeof this.value === 'string' || typeof this.value === 'number'
        ? +this.value
        : this.value!.value;

    if (!this.descontoControl.dirty) return;

    const isResetRodaPe = Number(rodaPeValue) == 0 && this.descontoRodaPe.dirty;

    if (!rodaPeValue || isResetRodaPe) {
      this.oldValue = +value;
      sessionStorage.setItem(this.id, this.oldValue.toString());
    }
  }

  verifyOldValue(value: DescontoNumero | null) {
    if ((typeof value === 'string' && value.length === 0) || Number(value) === 0 || value === null) {
      const recoveryLastOldValue = sessionStorage.getItem(this.id) ?? 0;
      const maskedRecoveredValue = (+recoveryLastOldValue).toString();

      this.descontoControl.setValue(maskedRecoveredValue);
      //this.signal = maskedRecoveredValue.includes('-') ? '-' : '+';
      this.write(+recoveryLastOldValue);
    }
  }

  rodaPeValueChanges() {
    this.descontoRodaPe.valueChanges
      .pipe(
        untilDestroyed(this),
        tap((value) => {
          if (typeof value == 'string') {
            this.unconfirmedRodaPeValue = value;
            this.descontoService.descontoRodapeValue$.setValue({
              value: +value.replace(',', '.'),
              prefix: this.matPrefix,
            });
          } else {
            const i = typeof value == 'object' ? value?.value : value;
            this.unconfirmedRodaPeValue = i + '' ?? '0';
            this.descontoService.descontoRodapeValue$.setValue(value);
          }
        }),
        distinctUntilChanged(),
        tap((value) => this.verifyOldValue(value?.value ?? 0)),
        exhaustMap((value) => {
          return this.descontoService.rodaPeIsConfirmed$$.pipe(
            map(() => {
              return typeof value == 'object' ? (value?.value ?? '0') : value;
            }),
          );
        }),
        map<string, RodaPe>((value: string) => {
          const valueRodape = this.descontoService.rodaPeIsConfirmed$$.value
            ? this.unconfirmedRodaPeValue
            : value;
          const prefix =
            (sessionStorage.getItem('prefix') as DescontoPrefix) ?? '%';
          return this.createRodaPeObject(valueRodape, prefix);
        }),

        distinctUntilKeyChanged('value'),
      )
      .subscribe((value) => {
        if (!this.descontoService.rodaPeIsConfirmed$$.value) return;

        if (!value || !value.value) {
          return;
        }

        this.oldValue = sessionStorage.getItem(this.id)
          ? +sessionStorage.getItem(this.id)!
          : 0;

        if (this.matPrefix === 'R$') this.switchPrefix();

        if (value.prefix === 'R$' && this.oldValue >= 0) {
          this.setRodaPeRealValue(value);
          return;
        }

        if (value.prefix === '%' && this.oldValue >= 0) {
          this.setRodaPePercentual(value);
        }
      });
  }

  private setRodaPePercentual(value: RodaPe) {
    const { vlTotal } = this.descontoService.descontoTotalVlTotal$.value;
    let total;
    total = vlTotal;

    if (!isNaN(total) && +value.value > 0 && this.oldValue >= 0) {
      this.unitaryValue = +this.unitaryValue;

      const discountValue = +(this.unitaryValue * (this.oldValue / 100));

      const discountRodaPe =
        (this.unitaryValue - discountValue) * (+value.value / 100);
      const discountTotal = +discountRodaPe + +discountValue;

      const discountToPercent = (+discountTotal * 100) / this.unitaryValue;

      const descontoControl = discountToPercent.toString();

      const userStringify = sessionStorage.getItem('user');
      const user = userStringify && JSON.parse(userStringify);
      const hasMaxDiscount = descontoControl > user.percentualRodape;

      if (hasMaxDiscount) {
        const maxPercent = user.percentualRodape.toString();
        this.descontoControl.setValue((+maxPercent).toFixed(7));
        this.write(+maxPercent);

        return;
      }

      if (this.descontoControl.value === descontoControl) return;

      this.descontoControl.setValue((+descontoControl).toFixed(7));
      this.write(+descontoControl);
    }
  }

  private setRodaPeRealValue(value: RodaPe) {
    const { vlTotal } = this.descontoService.descontoTotalVlTotal$.value;
    let subTotalAux;
    subTotalAux = vlTotal;

    if (typeof value.value === 'string') {
      value.value = value.value.replace(',', '.');
    }

    const realValue = +subTotalAux - Number(value.value);

    const subTotalPercent = (realValue * 100) / +subTotalAux;

    const rodaPePercent = +(100 - +(+subTotalPercent));

    value.value = rodaPePercent;

    if (subTotalAux && value.value > 0 && this.oldValue >= 0) {
      const discountValue = +(this.unitaryValue * (this.oldValue / 100));

      const discountRodaPe = +(
        (this.unitaryValue - discountValue) *
        (+value.value / 100)
      );
      const discountTotal = +discountRodaPe + +discountValue;

      const discountToPercent = +((+discountTotal * 100) / this.unitaryValue);

      const descontoControl = discountToPercent.toString();

      const userStringify = sessionStorage.getItem('user');
      const user = userStringify && JSON.parse(userStringify);
      const hasMaxDiscount = +descontoControl > user.percentualRodape;

      if (hasMaxDiscount) {
        const maxPercent = user.percentualRodape.toString();
        this.descontoControl.setValue(maxPercent);
        this.write(+maxPercent);

        return;
      }

      if (this.descontoControl.value === descontoControl) return;

      this.descontoControl.setValue(Number(descontoControl).toFixed(7));
      this.write(Number(descontoControl));
    }
  }

  private setMaxDiscounts() {
    this.currentDiscount = 0;

    this.updateMaximumDiscountValues();

    if (!this.maxPercentDiscount) return;

    const noRodaPeValue =
      this.descontoRodaPe.value === null ||
      this.descontoRodaPe.value?.length === 0 ||
      (typeof this.descontoRodaPe.value === 'number' &&
        +this.descontoRodaPe.value === 0) ||
      (typeof this.descontoRodaPe.value === 'object' &&
        +this.descontoRodaPe.value.value === 0);

    this.extractAuthorizationKeys(this.maxPercentDiscount);

    if (
      +this.descontoControl.value > this.maxPercent &&
      noRodaPeValue &&
      this.signal == '-'
    ) {
      const { hasToast, hasToastText, warningMessage } =
        this.createPercentageToast(this.maxPercent);
      this.oldValue = this.maxPercent;
      sessionStorage.setItem(this.id, this.oldValue.toString());
      this.descontoControl.setValue(this.maxPercent.toFixed(7));
      this.write(+this.maxPercent);

      if (hasToast && hasToastText)
        this.toast.showWarning(warningMessage, 1500);

      return;
    }

    const hasDiscountAutomatic = this.maxPercentDiscount.descautomatico === 'S';

    if (hasDiscountAutomatic && !this.oldValue) {
      this.descautomatico = this.maxPercentDiscount.descautomatico;
      this.currentDiscount = +this.maxPercent;
      this.oldValue = +this.maxPercent;
      sessionStorage.setItem(this.id, this.maxPercent.toString());
      this.descontoControl.setValue(this.maxPercent.toFixed(7));
    }

    const oldValue = sessionStorage.getItem(this.id);

    const setOldValueCondition =
      oldValue && +oldValue > 0 && noRodaPeValue && !this.descontoControl.dirty;

    if (setOldValueCondition) {
      this.oldValue = +oldValue;
      this.currentDiscount = +oldValue;
      this.descontoControl.setValue((+oldValue).toFixed(7));
      this.write(+oldValue);
    }

    if (typeof this.oldValue === 'number' && this.oldValue >= 0) return;
  }

  private updateMaximumDiscountValues() {
    if (!this.maxPercentDiscount) {
      this.maxPercent = 0;
      this.maxReal = 0;
      return;
    }

    const authorizedDiscount = +this.maxPercentDiscount.percdescautor;
    this.maxPercent =
      authorizedDiscount > 0
        ? authorizedDiscount
        : +this.maxPercentDiscount.percdesc || 0;
    this.extractMaxDiscountReal();
  }

  createRodaPeObject(
    value: string | RodaPe | unknown,
    prefix: DescontoPrefix,
  ): RodaPe {
    if (!value) {
      return {
        value: '0.00',
        prefix: prefix,
      };
    }

    if (typeof value === 'string') {
      return {
        value: value as string,
        prefix: prefix,
      };
    }
    const rodaPe = value as RodaPe;
    if (typeof rodaPe.prefix == 'string') rodaPe.prefix = prefix;

    return rodaPe;
  }

  hasRodaPeDiscount() {
    const isStringOrNumber =
      this.descontoRodaPe.value &&
      typeof this.descontoRodaPe.value == 'string' &&
      this.descontoRodaPe.value.includes(',');

    if (isStringOrNumber) {
      const replacedValue = this.descontoRodaPe.value.replace(',', '.');
      if (+replacedValue > 0) return true;
    }
    const hasDiscountRodaPe = +this.descontoRodaPe.value.value != 0;
    if (hasDiscountRodaPe) return true;

    const isRodaPeObject =
      typeof this.descontoRodaPe.value === 'object' &&
      this.descontoRodaPe.value &&
      this.descontoRodaPe.value.value &&
      +this.descontoRodaPe.value.value > 0;

    if (isRodaPeObject) return true;

    return false;
  }

  getDescontoRodaPeValue() {
    const prefix = sessionStorage.getItem('prefix') ?? '%';

    if (!prefix) {
      sessionStorage.setItem('prefix', '%');
    }
    const userStringify = sessionStorage.getItem('user');
    const user = userStringify && JSON.parse(userStringify);

    const isRodaPeObject =
      this.descontoRodaPe.value &&
      typeof this.descontoRodaPe.value === 'object' &&
      this.descontoRodaPe.value.value;

    const isStringOrNumber =
      typeof this.descontoRodaPe.value === 'string' ||
      typeof this.descontoRodaPe.value === 'number';

    if (prefix === 'R$') {
      if (isRodaPeObject) {
        return +this.conversionUnitaryToPercentSubTotal(
          this.descontoRodaPe.value.value,
        );
      }
      if (isStringOrNumber) {
        return +this.conversionUnitaryToPercentSubTotal({
          value: this.descontoRodaPe.value.value,
          prefix: 'R$',
        });
      }
    }

    const rodaPeValue = !isNaN(+this.descontoRodaPe.value.value)
      ? +this.descontoRodaPe.value.value
      : +this.descontoRodaPe.value.value;

    return rodaPeValue >= user.percentualRodape
      ? user.percentualRodape
      : rodaPeValue;
  }

  validationDescontoControl(value: string | number) {
    const realPrefix = 'R$';
    const percentPrefix = '%';
    const timeToWrite = 150;

    if (this.matPrefix === realPrefix && value !== this.lastRealValue) {
      this.validationDescontoReal(value, timeToWrite);
      return;
    }

    if (this.matPrefix === percentPrefix && value !== this.lastPercentValue) {
      this.validationDescontoPercent(value, timeToWrite);
    }
  }

  private validationDescontoReal(value: string | number, timeToWrite?: number) {
    if (
      (this.signal == '-' && +value < 0) ||
      (this.signal == '+' && +value > 0)
    ) {
      value = +value * -1;
    }

    this.lastRealValue = value.toString();

    const removeComma = value.toString().replace(',', '.');

    if (!this.maxReal || this.maxReal <= 0) {
      this.maxReal = 0;
    }

    if (this.hasRodaPeDiscount()) {
      const maxPercent = +this.maxPercent + this.getDescontoRodaPeValue();
      this.extractMaxDiscountReal(maxPercent);
    }

    const valueGreaterThanHundredPercent =
      +value > this.maxReal ? this.maxReal : removeComma;

    const descontoControlValue = isNaN(+valueGreaterThanHundredPercent)
      ? '0'
      : valueGreaterThanHundredPercent;

    if (this.router.url.includes('compra-finalizada')) return;

    const hasDiscountInPage =
      this.router.url.includes('/carrinho') ||
      this.router.url.includes('editar-orcamento');

    if (!hasDiscountInPage) return;

    this.lastRealValue = descontoControlValue.toString();

    this.currentDiscount = +descontoControlValue;

    this.lastRealValue = descontoControlValue.toString();

    if (+value > this.maxReal) {
      const { hasToast, hasToastText, warningMessage } = this.createRealToast();
      this.write(+this.maxReal);
      this.descontoControl.setValue((+this.maxReal).toFixed(7));
      sessionStorage.setItem(this.id, this.maxPercent.toString());
      if (hasToast && hasToastText)
        this.toast.showWarning(warningMessage, 1500);
      return;
    }
    const discountToPercent = +(
      (+valueGreaterThanHundredPercent * 100) /
      this.unitaryValue
    );
    sessionStorage.setItem(this.id, discountToPercent.toString());

    this.write(+descontoControlValue);
  }

  private createRealToast() {
    const warningMessage = `O DESCONTO não pode ser maior que R$ ${this.maxReal} Alterado para o valor permitido!`;

    const hasToast = document.querySelectorAll(
      '.bs.warning .hot-toast-message dynamic-view div',
    );

    const hasToastText = Array.from(hasToast).every((toastText) => {
      return toastText?.innerHTML !== warningMessage;
    });
    return { hasToast, hasToastText, warningMessage };
  }

  private validationDescontoPercent(
    value: string | number,
    timeToWrite: number,
  ) {
    if (!value) value = 0;

    if (
      (this.signal == '-' && +value < 0) ||
      (this.signal == '+' && +value > 0)
    ) {
      value = +value * -1;
    }

    this.lastPercentValue = value.toString();

    const hasPercent = this.matPrefix.includes('%');
    const addPercentage = `${value}${hasPercent ? '' : this.matPrefix}`;
    const removePercent = addPercentage.replace('%', '');
    const removeComma = removePercent.replace(',', '.');

    if (!this.maxPercent || this.maxPercent <= 0) {
      this.maxPercent = 0;
    }

    const maxPercent = this.hasRodaPeDiscount()
      ? +this.maxPercent + this.getDescontoRodaPeValue()
      : this.maxPercent;

    const valueGreaterThanHundredPercent =
      +removeComma > maxPercent ? maxPercent : addPercentage;

    const descontoControlValue = isNaN(+valueGreaterThanHundredPercent)
      ? '0'
      : valueGreaterThanHundredPercent;

    if (this.router.url.includes('compra-finalizada')) return;

    const hasDiscountInPage =
      this.router.url.includes('/carrinho') ||
      this.router.url.includes('editar-orcamento');

    if (!hasDiscountInPage) return;

    this.lastRealValue = descontoControlValue.toString();

    this.currentDiscount = +descontoControlValue;

    if (Number(value) > maxPercent) {
      const { hasToast, hasToastText, warningMessage } =
        this.createPercentageToast(maxPercent);

      if (hasToast && hasToastText && this.descontoControl.dirty)
        this.toast.showWarning(warningMessage, 1500);
      const maskedValue = +maxPercent.toString();

      this.write(Number(maskedValue));
      this.descontoControl.setValue(Number(maskedValue).toFixed(7));

      return;
    }

    this.descontoControl.setValue(Number(value));
    this.write(Number(value));
    sessionStorage.setItem(this.id, value.toString());
  }

  private createPercentageToast(maxPercent: number) {
    const warningMessage = `O DESCONTO não pode ser maior que ${
      maxPercent ?? 0
    }%. Alterado para o valor máximo!`;

    const hasToast = document.querySelectorAll(
      '.bs.warning .hot-toast-message dynamic-view div',
    );

    const hasToastText = Array.from(hasToast).every((toastText) => {
      return toastText?.innerHTML !== warningMessage;
    });
    return { hasToast, hasToastText, warningMessage };
  }

  private conversionUnitaryToPercentSubTotal(value: RodaPe) {
    const hasDoubleComma =
      value.value.toString().length >= 6 &&
      value.value.toString().match(/\./g)?.length === 2;
    if (hasDoubleComma) {
      const replaceReal = value.value.toString().replace('.', '');
      const valueNumber = replaceReal.substring(0, replaceReal.length - 2);
      const valueFloat = replaceReal.substring(replaceReal.length - 2);
      const realValue = `${valueNumber}.${valueFloat}`;

      value.value = realValue;

      if (value.value.includes('..')) {
        value.value = value.value.replace('..', '.');
      }
    }

    const numberValue = +value.value.toString().replace(',', '.');

    const expression =
      +sessionStorage.getItem('unitaryValueWithDiscount')! - numberValue;

    const multiplyExpression = expression * 100;

    const percentage =
      multiplyExpression / +sessionStorage.getItem('unitaryValueWithDiscount')!;

    return 100 - percentage;
  }

  private extractMaxDiscountReal(maxPercent?: number) {
    const maxInReal =
      this.unitaryValue * ((maxPercent ? maxPercent : this.maxPercent) / 100);

    const numberToFixed = maxInReal;

    const greaterThanToFixed = maxInReal > +numberToFixed ? 0.0 : 0.0;

    const equalsZero = numberToFixed === 0.0;

    const roundDown = equalsZero
      ? numberToFixed
      : +numberToFixed - greaterThanToFixed;

    this.maxReal = +roundDown;
  }

  private extractAuthorizationKeys(maxPercentDiscount: ProdutoDesconto) {
    const numautorizacao = sessionStorage.getItem('numautorizacao');

    const numaturozicaolist =
      (numautorizacao && JSON.parse(numautorizacao)) || [];

    const hasNumautorizacao =
      maxPercentDiscount.numautorizacao &&
      maxPercentDiscount.numautorizacao > 0 &&
      !numaturozicaolist.includes(maxPercentDiscount.numautorizacao);

    if (hasNumautorizacao) {
      numaturozicaolist.push(maxPercentDiscount.numautorizacao);
      const numautorizacaoStringify = JSON.stringify(numaturozicaolist);
      sessionStorage.setItem('numautorizacao', numautorizacaoStringify);
    }
  }

  private writeDescontoControl(value: string | number) {
    value = Number(value);

    if (!value) {
      this.write(0);
      this.descontoControl.setValue(0);
      return;
    }
    this.validationDescontoControl(value);
  }

  private write(value: number) {
    this.onChange({ value, prefix: this.matPrefix });
    this.writeValue({
      value: value,
      prefix: this.matPrefix,
    });
  }

  private get productPrice(): number {
    return Number(Number(this.unitaryValue).toFixed(3));
  }

  private syncProductInput() {
    if (!Number.isFinite(this.productPercentage)) this.productPercentage = 0;
    if (this.productPercentage !== 0)
      this.signal = this.productPercentage < 0 ? '+' : '-';
    const value = percentageToDiscount(
      this.productPercentage,
      this.matPrefix,
      this.productPrice,
    );
    this.descontoControl.setValue(roundDiscount(Math.abs(value)), {
      emitEvent: false,
    });
    this.changeDetectorRef.markForCheck();
  }

  private interactionBlocked(): boolean {
    if (this.disabled) return true;
    if (!this.blocked) return false;
    this.blockedInteraction.emit();
    return true;
  }

  private commitProductPercentage(percentage: number) {
    const maxDiscount = Math.max(
      0,
      Number(this.maxPercentDiscount?.percdesc) || 0,
    );
    const limited = Math.min(maxDiscount, Math.max(-999, percentage));
    if (limited !== percentage) {
      const label = percentage < 0 ? 'ACRÉSCIMO' : 'DESCONTO';
      const limit = percentage < 0 ? 999 : maxDiscount;
      this.toast.showWarning(
        `O ${label} não pode ser maior que ${limit}%. Alterado para o valor permitido!`,
        1700,
      );
    }
    this.productPercentage = roundDiscount(limited);
    this.sellingValueControl?.setValue(
      roundDiscount(this.productPrice * (1 - this.productPercentage / 100)),
      { emitEvent: false },
    );
    this.onChange(-this.productPercentage);
    this.syncProductInput();
  }

  applySellingValue() {
    if (this.context !== 'product' || this.interactionBlocked()) return;
    const price = this.productPrice;
    const sellingPrice = parseDiscount(this.sellingValueControl?.value);
    if (
      !Number.isFinite(sellingPrice) ||
      !Number.isFinite(price) ||
      price <= 0
    ) {
      this.toast.showWarning(
        'Informe um valor numérico válido e um preço de produto maior que zero.',
      );
      return;
    }
    this.commitProductPercentage((1 - sellingPrice / price) * 100);
    this.onTouched();
  }

  private confirmStandalone(): boolean {
    if (this.interactionBlocked()) return false;
    const value = parseDiscount(this.descontoControl.value);
    const price =
      this.context === 'product' ? this.productPrice : this.unitaryValue;
    if (
      !Number.isFinite(value) ||
      (this.matPrefix === 'R$' && (!Number.isFinite(price) || price <= 0))
    ) {
      this.toast.showWarning(
        'Informe um valor numérico válido e um preço de produto maior que zero.',
      );
      if (this.context === 'product') this.syncProductInput();
      else this.writeValue(this.value);
      return false;
    }
    if (this.context === 'product') {
      const percentage = discountToPercentage(
        signedDiscount(value, this.signal),
        this.matPrefix,
        price,
      );
      this.commitProductPercentage(percentage);
    } else {
      const result = { value: Math.abs(value), prefix: this.matPrefix };
      this.prefixChange.emit(this.matPrefix);
      this.write(result.value);
      this.confirmed.emit(result);
    }
    this.onTouched();
    return true;
  }

  switchPrefix() {
    if (this.context !== 'cart') {
      if (!this.confirmStandalone()) return;
      const price =
        this.context === 'product' ? this.productPrice : this.unitaryValue;
      if (!Number.isFinite(price) || price <= 0) {
        this.toast.showWarning(
          'O preço deve ser maior que zero para converter o desconto.',
        );
        return;
      }
      const percentage = discountToPercentage(
        parseDiscount(this.descontoControl.value),
        this.matPrefix,
        price,
      );
      this.matPrefix = this.matPrefix === '%' ? 'R$' : '%';
      this.descontoControl.setValue(
        roundDiscount(percentageToDiscount(percentage, this.matPrefix, price)),
        { emitEvent: false },
      );
      if (this.context === 'footer') this.confirmStandalone();
      return;
    }
    if (this.disabled) return;
    if (this.hasRodaPeDiscount()) return;
    const currentControlValue = this.descontoControl.value.toString();

    this.matPrefix =
      this.matPrefix === this.realPrefix ? this.percentPrefix : this.realPrefix;

    const removeComma = currentControlValue.toString().replace(',', '.');
    const calcTransformedPercentage =
      this.matPrefix === this.realPrefix
        ? removeComma
        : discountToPercentage(+removeComma, 'R$', this.unitaryValue);
    const toFixedPercentage = calcTransformedPercentage;
    const contPrefix = this.descontoService.PrefixRodapeCont$;

    if (contPrefix.value > 0 && this.matPrefix == '%') {
      contPrefix.setValue(contPrefix.value - 1);
    }
    if (this.matPrefix === this.realPrefix) {
      contPrefix.setValue(contPrefix.value + 1);
      const removePercent = currentControlValue.replace('%', '');
      const removePercentComma = removePercent.replace(',', '.');
      const calcTransformedReal = percentageToDiscount(
        +removePercentComma,
        'R$',
        this.unitaryValue,
      );
      const toFixedReal = calcTransformedReal;

      this.descontoControl.setValue(toFixedReal.toFixed(7).replace('-', ''));
      sessionStorage.setItem(this.id, toFixedPercentage.toString());
    } else {
      this.descontoControl.setValue(toFixedPercentage.toFixed(7));
      sessionStorage.setItem(this.id, toFixedPercentage.toString());
    }
    this.descontoControl.markAsDirty();
    this.isConfirmed$$.next(true);
    return;
  }

  switchSignal() {
    if (this.context !== 'cart') {
      if (this.context === 'footer' || !this.confirmStandalone()) return;
      this.signal = this.signal === '-' ? '+' : '-';
      this.confirmStandalone();
      return;
    }
    if (this.disabled || this.hasRodaPeDiscount()) return;
    this.signal = this.signal === '-' ? '+' : '-';
    this.isConfirmed$$.next(true);

    const normalizedValue = Number(
      this.descontoControl.value?.toString().replace(',', '.') ?? 0,
    );
    const discountValue = Number.isFinite(normalizedValue)
      ? normalizedValue
      : 0;
    const signedValue = signedDiscount(discountValue, this.signal);

    this.unconfirmedValue = signedValue;
    sessionStorage.setItem(
      this.id,
      this.signal === '+' && signedValue === 0 ? '-0' : signedValue.toString(),
    );
    this.descontoControl.setValue(
      signedValue === 0 ? 0 : signedValue.toFixed(7),
      { emitEvent: false },
    );
    this.write(signedValue);
  }

  applyDiscout(event: DescontoEvento) {
    if (this.disabled) return;
    if (this.context !== 'cart') {
      this.confirmStandalone();
      return;
    }
    this.setMaxDiscounts();
    const isRodaPeInput = (event.target as HTMLElement | null)?.id === 'descontoRodape';
    if (event.key === 'Enter' || event.type === 'focusout') {
      if (isRodaPeInput) {
        this.descontoService.rodaPeIsConfirmed$$.next(true);
        return;
      }
      this.isConfirmed$$.next(true);

      if (!this.hasRodaPeDiscount())
        this.descontoService.changeDescontoItem$$.next(true);

      if (
        (this.signal == '-' && +this.unconfirmedValue < 0) ||
        (this.signal == '+' && +this.unconfirmedValue > 0)
      ) {
        this.unconfirmedValue = Number(this.unconfirmedValue) * -1;
        const valueToSet = (+this.unconfirmedValue)
          .toFixed(7)
          .replace(',', '.');
        this.descontoControl.setValue(valueToSet);
      }
      if (this.matPrefix === 'R$') return;
      sessionStorage.setItem(this.id, String(this.unconfirmedValue));
      return;
    }
    if (
      event.key === 'Escape' &&
      document.activeElement instanceof HTMLInputElement
    ) {
      if (isRodaPeInput) {
        this.descontoRodaPe.setValue(this.descontoRodaPe.value);
        return;
      }
      this.descontoControl.setValue(this.oldValue.toFixed(7));
      return;
    }

    this.isConfirmed$$.next(false);

    if (isRodaPeInput && document.activeElement instanceof HTMLInputElement) {
      this.descontoService.rodaPeIsConfirmed$$.next(false);
      return;
    }
  }

  alertRodape() {
    if (this.context !== 'cart') {
      this.interactionBlocked();
      return;
    }
    if (!this.descontoRodaPe.value.value) return;
    this.toast.showInfo('Remova o desconto de rodape para alterar o valor');
  }

  getValueBudget(budget: ResquestBudget) {
    return this.orcamentoService
      .calculoOrcamento(budget)
      .pipe(map((resultado) => resultado));
  }
}
